import classNames from 'classnames';
import type {
  ForwardRefExoticComponent,
  HTMLAttributes,
  PropsWithChildren,
  RefAttributes,
} from 'react';

import React, { forwardRef, useMemo } from 'react';

import { Icon } from '../icon';

import styles from './carousel.scss';
import { useSlides } from './hooks';
import { countChildren } from './utils';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> &
  PropsWithChildren & {
    step?: number;
    allowScroll?: boolean;
    dataTestId?: string;
    autoScrollInterval?: number;
    onScrollLeft?: () => void;
    onScrollRight?: () => void;
    infinite?: boolean;
    pauseOnHover?: boolean;
    initialSlideIndex?: number;
  };

const COMPONENT_ID = 'modulor-carousel';

export const Carousel2: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      step = 1,
      allowScroll = true,
      dataTestId = COMPONENT_ID,
      className,
      autoScrollInterval,
      onScrollLeft,
      onScrollRight,
      initialSlideIndex = 0,
      infinite = false,
      pauseOnHover = false,
      ...rest
    },
    ref,
  ) => {
    const itemsCount = useMemo(() => countChildren(children), [children]);
    const isInfiniteCarousel = useMemo(() => itemsCount > 1 && infinite, [itemsCount, infinite]);

    const { slideBack, slideForward, canSlideBack, canSlideForward, carouselRef } = useSlides({
      step,
      itemsCount,
      onScrollLeft,
      onScrollRight,
      ref,
      autoScrollInterval,
      infinite: isInfiniteCarousel,
      pauseOnHover,
      initialSlideIndex,
    });

    return (
      <div
        className={classNames(className, styles.wrapper)}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        {...rest}>
        {canSlideBack && (
          <span
            className={classNames(styles.button, styles['button-left'])}
            onClick={slideBack}
            data-id={`${dataTestId}-button-left`}
            data-testid={`${dataTestId}-button-left`}>
            <Icon name="chevron_left" fill="icon-main" />
          </span>
        )}
        {canSlideForward && (
          <span
            className={classNames(styles.button, styles['button-right'])}
            onClick={slideForward}
            data-id={`${dataTestId}-button-right`}
            data-testid={`${dataTestId}-button-right`}>
            <Icon name="chevron_right" fill="icon-main" />
          </span>
        )}

        <div
          className={classNames(styles['slides-wrapper'], {
            [styles['allow-scroll']]: allowScroll,
          })}
          data-id={`${dataTestId}-items-container`}
          data-testid={`${dataTestId}-items-container`}
          ref={carouselRef}>
          {isInfiniteCarousel && children}
          {children}
          {isInfiniteCarousel && children}
        </div>
      </div>
    );
  },
);

Carousel2.displayName = 'Carousel2';
